import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNostrPublish } from './useNostrPublish';
import { formatArticleContent, type ArticleFormData } from '@/lib/types';
import { SITE_TAG } from '@/lib/categories';

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function usePublishArticle() {
  const { mutateAsync: createEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ArticleFormData & { existingSlug?: string }) => {
      const slug = data.existingSlug || slugify(data.title);
      const now = Math.floor(Date.now() / 1000);

      const content = formatArticleContent(data.content, data.quiz);

      const tags: string[][] = [
        ['d', slug],
        ['title', data.title],
        ['summary', data.summary],
        ['published_at', now.toString()],
        ['t', SITE_TAG],
        ['t', data.category],
      ];

      if (data.image) {
        tags.push(['image', data.image]);
      }

      const event = await createEvent({
        kind: 30023,
        content,
        tags,
        created_at: now,
      });

      return { event, slug };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      queryClient.invalidateQueries({ queryKey: ['latest-articles'] });
    },
  });
}
